import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { DrFileMeta, DrFileMetaCategoryList, FileUploadStatus } from '@app/shared/models/dr-file';
import {
    DistributionJobUriBuilderParams,
    DistributionService
} from '@app/core/services/distribution.service';
import {
    DistributionDocument,
    DistributionDocumentState,
    DistributionJob,
    DistributionJobChannel,
    DistributionJobChannels,
    DistributionJobState,
    DistributionPlaceholder,
    InsignState,
    isInSignDistributionJob,
} from '@app/shared/models/distribution-doc-models';
import { BaseWithIdList } from '@app/shared/models/list';
import { ParticipantsService } from '@app/core/services/participants.service';
import { Participant, Receiver } from '@app/shared/models/participant';
import { DistributionJobComponent } from '@app/shared/components/distribution-job/distribution-job.component';
import { TranslationEntry } from '@app/shared/models/translatable';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { SelectTemplatesDialogComponent } from '@app/shared/dialogs/select-templates-dialog/select-templates-dialog.component';
import { CaseFileCategories } from '@app/shared/models/case-file-category';


@Component({
    selector: 'distribution-job-list',
    templateUrl: './distribution-job-list.component.html',
    styleUrls: ['./distribution-job-list.component.scss']
})
export class DistributionJobListComponent extends BaseWithBundleComponent
                                          implements OnInit {

    @ViewChildren('djc')
    public distributionJobsComponents: QueryList<DistributionJobComponent>;

    @Input()
    public distributionJobList: BaseWithIdList<DistributionJob>;

    @Input()
    public activeDistributionJob: DistributionJob;

    @Input()
    public uriBuilderParamsFn: (job: DistributionJob) => DistributionJobUriBuilderParams;

    @Input()
    public externalReceiverList: BaseWithIdList<Receiver>;

    @Input()
    public externalUnfilteredAllAssignables: DrFileMetaCategoryList;

    @Input()
    public externalDistributionChannels: TranslationEntry[];

    @Input()
    public participants: BaseWithIdList<Participant>;

    @Output()
    public tellMyParents = new EventEmitter<any>();

    public unfilteredAllAssignables: DrFileMetaCategoryList;
    public allAssignables: DrFileMetaCategoryList;
    public distributionChannels: TranslationEntry[];
    public receiverList: BaseWithIdList<Receiver>;
    public inSignWindowRef: Window;

    get DistributionJobState() {
        return DistributionJobState;
    }

    constructor(
        protected componentServiceBundle: ComponentServiceBundle,
        protected distributionService: DistributionService,
        protected participantService: ParticipantsService
    ) {
        super(componentServiceBundle);
        this._idPrefix = 'jobs.'
    }

    public ngOnInit() {
        super.ngOnInit();
    }

    public getChannelTranslationKey(channel: string): string {
        const foundChannel = DistributionJobChannels.ALL.find(ch => ch.key === channel);
        return foundChannel ? foundChannel.trans : '';
    }

    private findDistributionJobComponent(id: string): DistributionJobComponent {
        return this.distributionJobsComponents.toArray().find(djc => djc.original.id === id);
    }

    public fileDropped(event: CdkDragDrop<string[]>): void {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else if (event.item.data && event.item.data.file && event.item.data.category) {
            this.addDocumentToDistributionJob(event.item.data.file);
        }
    }

    public openTemplatesDialog(): void {
        const dialogRef = this.dialog.open(SelectTemplatesDialogComponent, {
            width: '1200px',
            data: {
                templates: this.allAssignables,
                selectedDocuments: this.activeDistributionJob.documentsArray
            }
        });

        dialogRef.afterClosed()
                 .subscribe((selectedTemplates: DrFileMeta[]) => {
             if (selectedTemplates) {
                 selectedTemplates.forEach(template => {
                     this.addDocumentToDistributionJob(template);
                 });
             }
        });
    }

    public addDocumentToDistributionJob(fileMeta: DrFileMeta): void {
        if (this.activeDistributionJob && this.activeDistributionJob.state === DistributionJobState.INITIAL) {
            const activeDistributionJobComponent = this.findDistributionJobComponent(this.activeDistributionJob.id);

            let doc: DistributionDocument = fileMeta.is_placeholder ? new DistributionPlaceholder() : new DistributionDocument();

            if (fileMeta.is_placeholder) {
                doc.is_placeholder = fileMeta.is_placeholder;
            } else if (fileMeta.is_template) {
                doc.is_template = fileMeta.is_template;
            }

            if (!fileMeta.is_placeholder) {
                doc.document_key = fileMeta.document_key;
                doc.display_name = fileMeta.filename;
            }

            doc.document_type = fileMeta.document_type as CaseFileCategories;
            doc.properties = fileMeta.properties;
            doc.sort_order = activeDistributionJobComponent.original.nextOrder;
            doc.state = DistributionDocumentState.INITIAL;
            doc.fileUploadStatus = FileUploadStatus.LOADING;

            activeDistributionJobComponent.addDocument(doc);

            this.distributionService.addDocumentToDistributionJob(this.uriBuilderParamsFn(activeDistributionJobComponent.original), doc)
                .subscribe((result: DistributionDocument) => {
                    result.fileUploadStatus = FileUploadStatus.READY;
                    activeDistributionJobComponent.addDocument(result);
                    this.updateActiveDistributionJob(activeDistributionJobComponent.job);
                }, err => {
                    this.handleError(err);
                });
        }
    }

    public updateActiveDistributionJob(job: DistributionJob, fromChild: boolean = false): void {
        this.refreshActiveInListAndChild(job, fromChild);
        this.activeDistributionJob = this.distributionJobList.getItem(job.id);
        this.filterAllAssignables();
    }

    public selectDistributionJob(job: DistributionJob, alwaysSelect?: boolean): void {
        if (alwaysSelect) {
            this.activeDistributionJob = job;
        } else {
            this.activeDistributionJob = job && (!this.activeDistributionJob || job.id !== this.activeDistributionJob.id) ? job : null;
        }

        if (this.activeDistributionJob) {
            const distributionJobUriBuilder = this.uriBuilderParamsFn(this.activeDistributionJob);

            this.unfilteredAllAssignables = null;
            this.receiverList = null;
            this.distributionChannels = null;

            if (this.externalUnfilteredAllAssignables) {
                this.unfilteredAllAssignables = this.externalUnfilteredAllAssignables;
                this.filterAllAssignables();
            } else {
                this.distributionService.getDistributionJobItemAssignables(distributionJobUriBuilder)
                                        .subscribe((result) => {
                    this.unfilteredAllAssignables = result;
                    this.filterAllAssignables();
                }, err => this.handleError(err));
            }

            if (this.externalReceiverList) {
                this.receiverList = this.externalReceiverList;
            } else {
                this.distributionService.getDistributionJobItemReceivers(distributionJobUriBuilder)
                                        .subscribe((result) => {
                    this.receiverList = result;

                    if (this.participants) {
                        this.receiverList.items.forEach(rec => {
                            const part = this.participants.getItem(rec.id);
                            rec.type = part ? part.type.slice() : rec.type;
                            rec.actionListDisplayName = this.participantService.getTranslatedParticipantTypes(rec);
                        });
                    }
                }, err => this.handleError(err));
            }

            if (this.externalDistributionChannels) {
                this.distributionChannels = this.externalDistributionChannels;
            } else {
                this.distributionService.getDistributionJobChannels(distributionJobUriBuilder)
                                        .subscribe((result) => {
                    this.distributionChannels = result;
                }, err => this.handleError(err));
            }
        } else {
            this.allAssignables = new DrFileMetaCategoryList();
            this.receiverList = new BaseWithIdList(Receiver);
            this.tellMyParents.emit();
        }
    }

    public filterAllAssignables(): void {
        if (this.unfilteredAllAssignables && this.activeDistributionJob) {
            const allAssignables = new DrFileMetaCategoryList(this.unfilteredAllAssignables.items);

            switch (this.activeDistributionJob.channel) {
                case DistributionJobChannel.SIGNING: {
                    // Regular files cannot be added to an inSign job
                    allAssignables.items.forEach(fileMetaList => {
                        fileMetaList.entries.forEach(fileMeta => {
                            fileMeta.not_selectable = !fileMeta.properties || fileMeta.properties.signable !== 'true';
                        });
                    });
                    break;
                }
                default: {
                    // All files can be added
                    allAssignables.items.forEach(fileMetaList => {
                        fileMetaList.entries.forEach(fileMeta => {
                            fileMeta.not_selectable = false;
                        });
                    });
                    break;
                }
            }

            this.allAssignables = allAssignables;
            this.tellMyParents.emit();
        }
    }

    public updateListAndActiveJob(job: DistributionJob): void {
        this.refreshActiveInListAndChild(job);
        this.activeDistributionJob = this.distributionJobList.getItem(job.id);
        this.filterAllAssignables();
    }

    public deleteDistribution(jobToDelete: DistributionJob): void {
        this.distributionService.delete(this.uriBuilderParamsFn(jobToDelete))
                                .subscribe(result => {
            this.selectDistributionJob(null);
            this.distributionJobList.removeItem(jobToDelete);
        }, err => this.handleError(err));
    }

    public copyDistribution(job: DistributionJob): void {
        this.distributionService.copyDistributionJob(this.uriBuilderParamsFn(job))
                                .subscribe(result => {
            this.distributionJobList.addItem(result, true);

            setTimeout(() => {
                this.selectDistributionJob(result, true);
            }, 0);
        }, err => this.handleError(err));
    }

    public prepareDistribution(job: DistributionJob): void {
        this.distributionService.prepareDistributionJob(this.uriBuilderParamsFn(job))
            .subscribe(result => {
                this.activeDistributionJob.state = result.state;
                this.refreshActiveInListAndChild();
            }, err => this.handleError(err));
    }

    public signDistribution(job: DistributionJob): void {
        if (isInSignDistributionJob(job) && job.insign_send_now) {
            if ((!job.state || job.state === DistributionJobState.INITIAL) && (!job.insign_state || job.insign_state === InsignState.NONE)) {
                this.inSignWindowRef = window.open();
                this.prepareDistribution(job);
            } else if (job.insign_state !== InsignState.FINISHED && job.insign_link) {
                this.openInSignPage();
            }
        }
    }

    public sendDistribution(job: DistributionJob): void {
        this.distributionService.sendDistributionJob(this.uriBuilderParamsFn(job))
            .subscribe(result => {
                this.activeDistributionJob.state = result.state;
                this.refreshActiveInListAndChild();
            }, err => this.handleError(err));
    }

    public openInSignPage() {
        if (isInSignDistributionJob(this.activeDistributionJob) &&
            this.activeDistributionJob.insign_send_now &&
            this.activeDistributionJob.insign_state !== InsignState.FINISHED) {
            this.inSignWindowRef = this.inSignWindowRef || window.open();
            this.inSignWindowRef.location.href = this.activeDistributionJob.insign_link;
        }
    };

    public getJobStateIcon(job: DistributionJob): string {
        let prefix = 'fas fa-lg ';

        if (job.state === DistributionJobState.ERROR_PREPARING || job.state === DistributionJobState.ERROR_SENDING) {
            prefix += 'iq-color-warn-dark';
        } else if (this.activeDistributionJob && this.activeDistributionJob.id === job.id) {
            prefix += 'iq-color-white';
        } else {
            prefix += 'iq-color-3';
        }

        return `${prefix} ${job.getIcon()}`;
    }

    private refreshActiveInListAndChild(job?: DistributionJob, skipChildReInitOriginalAndUpdated: boolean = false): void {
        job = job || this.activeDistributionJob;
        const activeInList = this.distributionJobList.getItem(job.id);
        activeInList.initialize(job);

        if (!skipChildReInitOriginalAndUpdated) {
            let activeJobComponent = this.findDistributionJobComponent(job.id);

            if (activeJobComponent) {
                activeJobComponent.reInitOriginalAndUpdated(job);
            }
        }
    }
}
